import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import {
  FEEDBACK_CATEGORIES, useSendFeedbackApi, buildSelfExcludedEmployeeAsync, useFeedbackRequestContext,
} from './config';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';

// Task 31 — Sürekli Geri Bildirim: gönderim ekranı. Kendi kendine gönderim
// (selfNotAllowed) ve kategori zorunluluğu (categoryRequired) backend'de
// zaten reddediliyor — burada yalnız kullanıcı deneyimi için istemci
// tarafında ön kontrol (mevcut çalışan arama sonuçlarından kendi kaydını
// eleme + boş kategori/alıcıyla gönder butonunu pasifleştirme). Gerçek
// doğrulama kaynağı her zaman backend'dir.
const Container = styled.div``;

const PageHeader = styled.div`
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 24px;
  max-width: 640px;
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const NoticeBanner = styled.div`
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SentBanner = styled.div`
  background: #E8F7EE;
  border: 1px solid #C7ECD4;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #1B7A3E;
`;

// Faz D (spec 2026-08-08 §4, Task 5, D3) — `?requestId=` bağlam bandı: talep
// edenin adı + (varsa) talep mesajı. NoticeBanner'la AYNI mor/mavi olmayan
// ayrı bir ton (#FFF7ED/#FDBA74) — "bu form şu talebe yanıt olarak kilitli"
// vurgusu NoticeBanner'ın genel anonimlik uyarısıyla karışmasın diye.
const ContextBanner = styled.div`
  background: #FFF7ED;
  border: 1px solid #FED7AA;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #9A3412;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const EMPTY_FORM = { receiverEmployeeId: null, category: '', text: '', isAnonymous: false };

export default function SendFeedbackPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const { send, sending } = useSendFeedbackApi();

  const [form, setForm] = useState(EMPTY_FORM);
  const [sentInfo, setSentInfo] = useState(false);

  const categoryOptions = useMemo(() => FEEDBACK_CATEGORIES.map((c) => ({
    id: c,
    name: t(`performance.feedback.category.${c}`),
  })), [t]);

  // `GET v1/auth/me` (UserDetailResponse.EmployeeId — inceleme düzeltmesi, bkz.
  // AuthorizeDataAccess.GetLoggedUserDetail) artık gerçek Employee.Id'yi
  // döndürüyor. ÖNCEDEN `user?.id` (User.Id) fallback'i vardı — bu YANLIŞTI
  // (User.Id ≠ Employee.Id, filtre hiçbir zaman gerçek kendi kaydını
  // eşleştirmiyordu). Employee'siz kullanıcılarda (ör. bazı admin hesapları)
  // `employeeId` null'dır — bu durumda istemci tarafı filtre uygulanmaz,
  // gerçek koruma her zaman backend'in `selfNotAllowed` reddidir.
  const currentEmployeeId = user?.employeeId;

  const receiverAsync = useMemo(
    () => buildSelfExcludedEmployeeAsync(currentEmployeeId),
    [currentEmployeeId],
  );

  // Faz D (spec 2026-08-08 §4, Task 5, D3) — `?requestId=` ile açılınca alıcı
  // KİLİTLİ + bağlam bandı. Ayrı bir GetById ucu YOK (task-2-report.md §1);
  // useFeedbackRequestContext GET v1/feedback-requests/my'nin 'received'
  // listesinden requestId eşleşen PENDING kaydı bulur. Bulunamazsa (yabancı/
  // bayat talep) sessizce kilitsiz normal forma düşülür — foreign bir
  // requestId'yi manuel seçilmiş bir alıcıyla birlikte gövdeye eklemek
  // kafa karıştırıcı bir receiverMismatch/notFound'a yol açardı, bu yüzden
  // yalnız yerel olarak DOĞRULANMIŞ bağlam gövdeye taşınır.
  const requestId = useMemo(
    () => new URLSearchParams(location.search).get('requestId'),
    [location.search],
  );
  const { request: lockedRequest } = useFeedbackRequestContext(requestId);

  useEffect(() => {
    if (lockedRequest) {
      setForm((prev) => ({ ...prev, receiverEmployeeId: lockedRequest.requesterEmployeeId }));
    }
  }, [lockedRequest]);

  const handleChange = useCallback((field, value) => {
    setSentInfo(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const canSend = !!form.receiverEmployeeId && !!form.category && !sending;

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    const payload = {
      receiverEmployeeId: form.receiverEmployeeId,
      category: form.category,
      text: form.text?.trim() ? form.text.trim() : undefined,
      isAnonymous: form.isAnonymous,
    };
    // requestId?: string — köprü alanı, doluysa BE `POST /v1/continuous-feedback/my`
    // içinde talebi `completed`e taşır (task-2-report.md §1-2).
    if (lockedRequest) payload.requestId = lockedRequest.id;
    const ok = await send(payload);
    if (ok) {
      // Kilitli bağlamda alıcıyı EMPTY_FORM'un sıfırlamasından koru — form
      // sıfırlanır ama talebin hedefi (kilitli alıcı) ekranda kalır.
      setForm((prev) => ({ ...EMPTY_FORM, receiverEmployeeId: lockedRequest ? prev.receiverEmployeeId : null }));
      setSentInfo(true);
    }
  }, [canSend, form, send, lockedRequest]);

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('performance.feedback.sendTitle')}</PageTitle>
      </PageHeader>

      <Card>
        {sentInfo && <SentBanner>{t('performance.feedback.sentInfo')}</SentBanner>}

        {lockedRequest && (
          <ContextBanner data-role="request-context-banner">
            {lockedRequest.message
              ? t('feedbackRequests.sendContext.withMessage', {
                name: lockedRequest.requesterName,
                message: lockedRequest.message,
              })
              : t('feedbackRequests.sendContext.noMessage', { name: lockedRequest.requesterName })}
          </ContextBanner>
        )}

        <FieldGroup>
          <AsyncGenericSelect
            label={t('performance.feedback.receiver')}
            value={form.receiverEmployeeId}
            onChange={(v) => handleChange('receiverEmployeeId', v)}
            onSearch={receiverAsync.onSearch}
            onGetById={receiverAsync.onGetById}
            disabled={!!lockedRequest}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <GenericSelect
            label={t('performance.feedback.categoryLabel')}
            value={form.category}
            onChange={(v) => handleChange('category', v)}
            options={categoryOptions}
            idKey="id"
            labelKey="name"
            required
          />
        </FieldGroup>

        <FieldGroup>
          <TextInput
            label={t('performance.feedback.text')}
            type="textarea"
            rows={4}
            value={form.text}
            onChange={(e) => handleChange('text', e.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <CheckboxInput
            label={t('performance.feedback.isAnonymous')}
            checked={form.isAnonymous}
            onChange={(v) => handleChange('isAnonymous', v)}
          />
        </FieldGroup>

        <NoticeBanner>{t('performance.feedback.anonymityNotice')}</NoticeBanner>

        <Actions>
          <Button onClick={handleSend} disabled={!canSend} loading={sending}>
            {t('performance.feedback.send')}
          </Button>
        </Actions>
      </Card>
    </Container>
  );
}
